import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { statusBadgeClass, STEPS } from "../../components/instructor/InstructorShell";

export default function InstructorDashboard() {
  const { instructor, user, refreshInstructor } = useAuth();
  const profile = instructor?.profile;
  const completeness = instructor?.completeness;

  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(!instructor);

  const load = React.useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await refreshInstructor();
    } catch (err) {
      setError(err?.message || "We couldn't load your profile. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [refreshInstructor]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (!instructor || !profile) {
    if (error) {
      return (
        <div className="max-w-md rounded-2xl border border-red-100 bg-red-50 p-6">
          <p className="font-medium text-red-800">We couldn't load your profile</p>
          <p className="mt-1 text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="mt-4 inline-flex rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {loading ? "Retrying…" : "Try again"}
          </button>
          <p className="mt-3 text-xs text-red-700/80">
            If this keeps happening, contact info@yogaretreatsbooking.com.
          </p>
        </div>
      );
    }
    return <div className="text-gray-500">Loading your profile…</div>;
  }

  const nextMissing = completeness?.requiredMissing?.[0];
  const nextStep = STEPS.find((s) => s.id === nextMissing?.step) || STEPS[0];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-semibold text-gray-900">
          Welcome{profile.display_name ? `, ${profile.display_name.split(" ")[0]}` : ""}
        </h1>
        <p className="text-gray-600 mt-1">
          Finish your profile so students can find and trust you.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">Profile completeness</p>
              <p className="text-4xl font-semibold text-gray-900 mt-1">
                {completeness?.score ?? 0}%
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Submit requires {completeness?.threshold ?? 85}% and all required items.
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadgeClass(profile.status)}`}>
              {profile.status}
            </span>
          </div>
          <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500"
              style={{ width: `${completeness?.score ?? 0}%` }}
            />
          </div>

          {completeness?.missing?.length > 0 && (
            <ul className="mt-6 space-y-2">
              {completeness.missing.map((item) => (
                <li key={item.key} className="flex items-center justify-between text-sm">
                  <span className={item.optional ? "text-gray-500" : "text-gray-800"}>
                    {item.label}
                    {item.optional ? " (optional)" : ""}
                  </span>
                  <Link
                    to={`/instructor/onboarding/${item.step}`}
                    className="text-emerald-700 font-medium hover:underline"
                  >
                    Fix
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {["Draft", "Rejected", "Approved"].includes(profile.status) && (
              <Link
                to={nextStep.path}
                className="inline-flex rounded-lg bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-emerald-700"
              >
                {profile.status === "Approved"
                  ? "Edit profile"
                  : completeness?.score
                    ? "Continue onboarding"
                    : "Start onboarding"}
              </Link>
            )}
            {["Submitted", "UnderReview"].includes(profile.status) && (
              <Link
                to="/instructor/onboarding/review"
                className="inline-flex rounded-lg bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-emerald-700"
              >
                View application / withdraw to edit
              </Link>
            )}
            <Link
              to="/instructor/onboarding/review"
              className="inline-flex rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              {profile.status === "Rejected"
                ? "Preview & resubmit"
                : profile.status === "Approved" && profile.has_pending_review
                  ? "Preview & submit updates"
                  : "Preview & submit"}
            </Link>
            {profile.is_published && profile.slug && (
              <Link
                to={`/teachers/${profile.slug}`}
                className="inline-flex rounded-lg border border-emerald-200 text-emerald-800 px-4 py-2.5 text-sm font-medium hover:bg-emerald-50"
              >
                View public profile
              </Link>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900">Account</h2>
          <div className="text-sm text-gray-600 space-y-1">
            <p>{user?.email}</p>
            <p>
              Email{" "}
              {user?.email_verified ? (
                <span className="text-emerald-700">verified</span>
              ) : (
                <span className="text-amber-700">not verified</span>
              )}
            </p>
            {profile.has_pending_review && (
              <p className="text-amber-700">Sensitive edits awaiting admin review</p>
            )}
            {profile.rejection_reason && profile.status === "Rejected" && (
              <p className="text-red-700 mt-2">
                <span className="font-medium">Rejection reason:</span> {profile.rejection_reason}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
